import { useEffect } from 'react';
import { PRODUCTION_HOSTS } from '@/lib/schema';

/**
 * Drop this once per page (or once globally) with an array of schema
 * objects. We DO NOT render via JSX — instead we append/remove
 * `<script type="application/ld+json">` nodes on `document.head` so:
 *
 *  • non-production hosts never receive any schema (Google's preview
 *    crawl can't accidentally index two copies of our identity)
 *  • each navigation cleans up its previous schema (no JSON-LD
 *    accumulating across SPA route changes)
 *  • multiple components on the same page can stack schemas safely;
 *    each instance owns its own <script> nodes via a unique data-attr
 */
let _instanceCounter = 0;

export default function SchemaMarkup({ schemas, instanceId }) {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;

    // Production-only gate. The user explicitly asked us to keep schema
    // off the Emergent preview URL — Google should only see this on
    // snap-windows.com.
    const host = window.location.hostname || '';
    if (!PRODUCTION_HOSTS.has(host)) return undefined;

    // Dedup-safe id so multiple SchemaMarkup instances on the same
    // page don't fight each other.
    const id = instanceId || `schema-${++_instanceCounter}`;
    const list = (Array.isArray(schemas) ? schemas : [schemas]).filter(Boolean);
    if (list.length === 0) return undefined;

    const scripts = list.map((obj, idx) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.dataset.schemaInstance = id;
      s.dataset.schemaIdx = String(idx);
      try {
        s.text = JSON.stringify(obj);
      } catch (_) {
        return null;
      }
      document.head.appendChild(s);
      return s;
    });

    return () => {
      scripts.forEach((s) => {
        if (s && s.parentNode) s.parentNode.removeChild(s);
      });
    };
  }, [schemas, instanceId]);

  return null;
}
